import React from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
  BaseTextFieldProps,
} from '@material-ui/core';
import clsx from 'clsx';
import MemoBaseTypography from '../Text/BaseTypography';
import { BaseTextField } from './BaseTextField';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sharpInput: {
      // customize the input height and padding
      '&.MuiTextField-root .MuiOutlinedInput-root': {
        borderRadius: 0, // sharp input has no border radius
      },
      '& .MuiInputBase-input': {
        '&:focus': {
          // hide placeholder on focus
          '&::placeholder': {
            opacity: 0,
          },
        },
      },
      zIndex: 2, // the input should be above the label element
    },
    '@keyframes showLabel': {
      '0%': {
        transform: 'translateY(100%)',
      },
      '50%': {
        transform: 'translateY(50%)',
      },
      '100%': {
        transform: 'translateY(0)',
        opacity: 1,
      },
    },
    '@keyframes hideLabel': {
      '0%': {
        transform: 'translateY(0)',
      },
      '50%': {
        transform: 'translateY(50%)',
      },
      '100%': {
        transform: 'translateY(100%)',
        opacity: 0,
      },
    },
    inputLabel: {
      zIndex: 1,
      background: theme.palette.primary.main,
      width: 'fit-content',
      color: 'white',
      padding: theme.spacing(0, 0.5),
    },
    // this animation class applies when the input is focused
    animateShowLabel: {
      animation: `$showLabel 300ms ${theme.transitions.easing.easeInOut}`,
      animationFillMode: 'forwards',
    },
    // this animation class applies when the input is blurred
    animateHideLabel: {
      animation: `$hideLabel 300ms ${theme.transitions.easing.easeInOut}`,
      animationFillMode: 'forwards',
    },
  }),
);

/**
 * This component is a TextField with sharp border.
 * It controls an animated label that appears when the input is focused.
 * @returns {React.FC<BaseTextFieldProps>}
 */
export const SharpOutlinedTextField = ({
  label,
  ...otherProps
}: BaseTextFieldProps) => {
  const classes = useStyles();
  const [showLabel, setShowLabel] = React.useState(false);
  return (
    <div>
      <MemoBaseTypography
        className={clsx(classes.inputLabel, {
          [classes.animateShowLabel]: showLabel,
          [classes.animateHideLabel]: !showLabel,
        })}
        fontType="10Medium"
      >
        {label}
      </MemoBaseTypography>
      <BaseTextField
        variant="outlined"
        className={classes.sharpInput}
        {...otherProps}
        onFocus={() => setShowLabel(true)}
        onBlur={() => setShowLabel(false)}
      />
    </div>
  );
};
